import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { Lane } from "./schemas"
import type { FeedbackContext } from "@/api/feedback-api"
import type { TFunction } from "i18next"

import { ChevronDown } from "@/components/icons"
import { cn } from "@/lib/utils"

type TransparencyBlockProps = {
  lane: Lane
  fields: Record<string, unknown>
  context: FeedbackContext
  evidencePreview?: { name: string; previewUrl: string } | null
  /** Auto-captured screenshot preview — rendered in the collapsible details section. */
  screenshotPreview?: { name: string; previewUrl: string } | null
}

/**
 * Shown in the review step. By default shows only the user's answers so
 * the review stays short — the auto-collected technical context + the
 * auto-captured screenshot are folded behind an "expand to see all
 * details" toggle so users who care can verify, and users who don't
 * aren't overwhelmed.
 */
export function TransparencyBlock({
  lane,
  fields,
  context,
  evidencePreview,
  screenshotPreview,
}: TransparencyBlockProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const userEntered = entries(fields).filter(
    // Blob paths are rendered as thumbnails, not table rows.
    ([k, v]) =>
      k !== "evidenceBlobPath" &&
      k !== "screenshotBlobPath" &&
      v !== null &&
      v !== undefined &&
      v !== ""
  )

  const autoEntries = buildAutoEntries(context, t)
  const identityEntries = buildIdentityEntries(context, t)
  const configSummary = summariseConfigSnapshot(context.app_config_snapshot)
  const hasTechnical =
    autoEntries.length > 0 ||
    identityEntries.length > 0 ||
    configSummary !== null ||
    screenshotPreview !== null
  const technicalCount =
    autoEntries.length +
    identityEntries.length +
    (configSummary ? 1 : 0) +
    (screenshotPreview ? 1 : 0)

  return (
    <div className="bg-muted/30 space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">{t("feedback.transparency.title", "What we'll send")}</p>
        <p className="text-muted-foreground text-xs">
          {t(
            "feedback.transparency.descriptionShort",
            "Your answers below. Technical context (browser, page, screenshot, etc.) is attached automatically."
          )}
        </p>
      </div>

      <Section
        empty={t("feedback.transparency.yourAnswersEmpty", "No answers provided.")}
        title={t("feedback.transparency.yourAnswers", "Your answers")}
      >
        {userEntered.map(([key, value]) => (
          <Row key={key} label={fieldLabel(lane, key, t)} value={renderValue(value)} />
        ))}
      </Section>

      {evidencePreview && (
        <Section title={t("feedback.transparency.evidence", "Evidence image")}>
          <div className="flex items-center gap-3">
            <img
              alt={evidencePreview.name}
              className="border-border max-h-24 rounded-md border object-cover"
              src={evidencePreview.previewUrl}
            />
            <span className="text-muted-foreground text-xs break-all">{evidencePreview.name}</span>
          </div>
        </Section>
      )}

      {hasTechnical && (
        <div className="border-border/60 -mx-4 -mb-4 border-t">
          <button
            aria-expanded={expanded}
            type="button"
            className={cn(
              "hover:bg-muted/50 flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-xs transition-colors",
              expanded && "bg-muted/40"
            )}
            onClick={() => setExpanded((v) => !v)}
          >
            <span className="text-muted-foreground">
              {expanded
                ? t("feedback.transparency.collapseDetails", "Hide technical details")
                : t("feedback.transparency.expandDetails", {
                    defaultValue: "Expand to see all details ({{count}})",
                    count: technicalCount,
                  })}
            </span>
            <ChevronDown
              className={cn(
                "text-muted-foreground size-4 transition-transform",
                expanded && "rotate-180"
              )}
            />
          </button>

          {expanded && (
            <div className="space-y-4 px-4 pt-2 pb-4">
              {identityEntries.length > 0 && (
                <Section title={t("feedback.transparency.identity", "Signed-in user")}>
                  {identityEntries.map(([label, value]) => (
                    <Row key={label} mono label={label} value={value} />
                  ))}
                </Section>
              )}

              {autoEntries.length > 0 && (
                <Section title={t("feedback.transparency.autoCollected", "Auto-collected context")}>
                  {autoEntries.map(([label, value]) => (
                    <Row key={label} mono label={label} value={value} />
                  ))}
                </Section>
              )}

              {configSummary && (
                <Section title={t("feedback.transparency.appConfig", "App configuration")}>
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "feedback.transparency.appConfigDescription",
                      "A snapshot of the app's config ({{count}} top-level keys) is attached so support can see what the app was using."
                    ).replace("{{count}}", String(configSummary.keyCount))}
                  </p>
                  {configSummary.preview.length > 0 && (
                    <p className="text-muted-foreground font-mono text-[11px] break-all">
                      {configSummary.preview.join(", ")}
                      {configSummary.keyCount > configSummary.preview.length ? ", …" : ""}
                    </p>
                  )}
                </Section>
              )}

              {screenshotPreview && (
                <Section title={t("feedback.transparency.screenshot", "Screenshot of your screen")}>
                  <img
                    alt={screenshotPreview.name}
                    className="border-border max-h-40 rounded-md border object-cover"
                    src={screenshotPreview.previewUrl}
                  />
                </Section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function buildAutoEntries(context: FeedbackContext, t: TFunction): Array<[string, string]> {
  const ctx = context.client_info
  const out: Array<[string, string]> = []
  if (context.session_id)
    out.push([t("feedback.transparency.sessionId", "Session ID"), context.session_id])
  if (context.message_id)
    out.push([t("feedback.transparency.messageId", "Message ID"), context.message_id])
  if (context.error_id)
    out.push([t("feedback.transparency.errorId", "Error ref"), context.error_id])
  if (ctx?.route) out.push([t("feedback.transparency.route", "Route"), ctx.route])
  if (ctx?.url) out.push([t("feedback.transparency.url", "Page URL"), ctx.url])
  if (ctx?.page_title)
    out.push([t("feedback.transparency.pageTitle", "Page title"), ctx.page_title])
  if (ctx?.referrer) out.push([t("feedback.transparency.referrer", "Came from"), ctx.referrer])
  if (ctx?.viewport) out.push([t("feedback.transparency.viewport", "Viewport"), ctx.viewport])
  if (ctx?.screen_resolution)
    out.push([
      t("feedback.transparency.screenResolution", "Screen resolution"),
      ctx.screen_resolution,
    ])
  if (ctx?.device_pixel_ratio)
    out.push([
      t("feedback.transparency.devicePixelRatio", "Pixel ratio"),
      String(ctx.device_pixel_ratio),
    ])
  if (ctx?.timezone) out.push([t("feedback.transparency.timezone", "Timezone"), ctx.timezone])
  if (ctx?.language) out.push([t("feedback.transparency.language", "Language"), ctx.language])
  if (ctx?.user_agent)
    out.push([t("feedback.transparency.userAgent", "Browser"), shortenUserAgent(ctx.user_agent)])
  if (ctx?.platform) out.push([t("feedback.transparency.platform", "Platform"), ctx.platform])
  if (ctx?.online !== undefined)
    out.push([t("feedback.transparency.online", "Online"), ctx.online ? "yes" : "no"])
  if (ctx?.connection_type)
    out.push([t("feedback.transparency.connectionType", "Connection"), ctx.connection_type])
  if (ctx?.color_scheme)
    out.push([t("feedback.transparency.colorScheme", "Theme"), ctx.color_scheme])
  if (ctx?.reduced_motion)
    out.push([t("feedback.transparency.reducedMotion", "Reduced motion"), "on"])
  if (ctx?.hardware_concurrency)
    out.push([
      t("feedback.transparency.hardwareConcurrency", "CPU cores"),
      String(ctx.hardware_concurrency),
    ])
  if (ctx?.device_memory)
    out.push([t("feedback.transparency.deviceMemory", "RAM (GB)"), String(ctx.device_memory)])
  return out
}

function buildIdentityEntries(context: FeedbackContext, t: TFunction): Array<[string, string]> {
  const id = context.identity_snapshot
  if (!id) return []
  const out: Array<[string, string]> = []
  const push = (key: string, label: string, value: unknown) => {
    if (value === null || value === undefined || value === "") return
    out.push([t(`feedback.transparency.${key}`, label), String(value)])
  }
  push("userOid", "User ID", id.oid)
  push("userTid", "Tenant", id.tid)
  push("userName", "Name", id.name)
  push("userPreferredUsername", "Username", id.preferred_username)
  push("userEmail", "Email", id.email)
  const groups = (id as { groups?: unknown }).groups
  if (Array.isArray(groups)) {
    const count = groups.length
    const label = t("feedback.transparency.userGroups", "Groups")
    const preview = groups
      .filter((g): g is string => typeof g === "string")
      .slice(0, 5)
      .join(", ")
    out.push([label, preview ? `${count} (${preview}${count > 5 ? ", …" : ""})` : String(count)])
  }
  const roles = (id as { roles?: unknown }).roles
  if (Array.isArray(roles) && roles.length > 0) {
    out.push([
      t("feedback.transparency.userRoles", "Roles"),
      roles.filter((r): r is string => typeof r === "string").join(", "),
    ])
  }
  return out
}

function summariseConfigSnapshot(
  snapshot: unknown
): { keyCount: number; preview: string[] } | null {
  if (!snapshot || typeof snapshot !== "object") return null
  const keys = Object.keys(snapshot as Record<string, unknown>)
  if (keys.length === 0) return null
  return { keyCount: keys.length, preview: keys.slice(0, 8) }
}

function Section({
  title,
  empty,
  children,
}: {
  title: string
  empty?: string
  children: React.ReactNode
}) {
  const childArray = Array.isArray(children) ? children : [children]
  const hasContent = childArray.some(Boolean)
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
        {title}
      </p>
      {hasContent ? (
        <div className="space-y-1.5">{children}</div>
      ) : (
        <p className="text-muted-foreground text-xs italic">{empty}</p>
      )}
    </div>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="border-border/50 flex items-start justify-between gap-3 border-b pb-1.5 last:border-b-0 last:pb-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span
        className={
          mono
            ? "max-w-[65%] text-right font-mono text-xs break-all"
            : "max-w-[65%] text-right text-xs break-words whitespace-pre-wrap"
        }
      >
        {value}
      </span>
    </div>
  )
}

function entries(obj: Record<string, unknown>): Array<[string, unknown]> {
  return Object.entries(obj)
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return ""
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return JSON.stringify(value)
}

function shortenUserAgent(ua: string): string {
  return ua.length > 80 ? ua.slice(0, 80) + "…" : ua
}

function fieldLabel(lane: Lane, key: string, t: TFunction): string {
  const ns = `feedback.fields.${lane}.${key}`
  // Keep humanised fallback so labels remain readable before translations land.
  const fallback = humanise(key)
  return t(ns, fallback)
}

function humanise(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}
